import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { css, type Theme } from '@emotion/react';
import { trpc, useQuery } from 'client/lib/trpc';
import { LogView } from 'client/components/Log/LogView';
import { useLog } from 'client/components/Log/useLog';

const LoadingPage = () => {
  return <div css={errorStyle}>Loading...</div>;
};

const ErrorPage = ({ message }: { message: string }) => {
  return <div css={errorStyle}>{message}</div>;
};

/** Standalone log page */
export const DeploymentLogPage = () => {
  const params = useParams();
  const projectID = params.projectID ? parseInt(params.projectID) : 0;
  const deploymentID = params.deploymentID ? parseInt(params.deploymentID) : null;
  const [startError, setStartError] = useState<string | null>(null);
  const windowUrl = useRef<string>('');

  const { activeLog, startLogFromUrlParameters } = useLog();
  const activeLogUrl = activeLog?.settings.url;

  // TODO: All kinds of error checking here, deployment not found, error loading, deployment stopped, deployment deleted etc.
  const deploymentQuery = useQuery(
    trpc.deployment.load,
    {
      id: deploymentID as number,
      projectID,
      cloneOfDeploymentID: null,
    },
    {
      allowedStatuses: ['ok', 'deployment not found'],
      enabled: !!deploymentID,
      retry: false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
    }
  );

  /*console.log(
    `useQuery hasDocument=${deploymentQuery.data?.document ? 'YES' : 'NO'} dID:${deploymentID} isLoading:${deploymentQuery.isLoading} isError:${deploymentQuery.isError} isFetching:${deploymentQuery.isFetching} fetchStatus:${deploymentQuery.fetchStatus} params: ${JSON.stringify(params)} `
  );*/

  const document = deploymentQuery.data?.status == 'ok' ? deploymentQuery.data?.document : null;
  const project = deploymentQuery.data?.status == 'ok' ? deploymentQuery.data?.project : null;

  // When we have a deployment document and new url, trigger load of new log
  useEffect(() => {
    (async () => {
      if (document && project) {
        const { accountIdentifier, id: projectID, name: projectName } = project;
        const { id: deploymentID, name: deploymentName, stage: deploymentStage, componentsWithValues } = document;

        if (windowUrl.current == document.url) return;
        windowUrl.current = document.url;
        if (windowUrl.current != activeLogUrl) {
          const { type, level1, level2, level3 } = params;

          const ret = await startLogFromUrlParameters({
            type: type || '',
            level1: level1 || '',
            level2,
            level3,
            deployment: {
              accountIdentifier,
              projectID,
              projectName,
              deploymentID,
              deploymentName,
              deploymentStage,
              components: componentsWithValues.map((c) => ({
                componentName: c.name,
                componentUrl: c.packageUrl || '',
                namespace: c.identifier,
              })),
            },
          });
          if (ret.status != 'ok') {
            // TODO: Render 404 and other error pages based on result
            console.log('DeploymentLogPage: unhandled error', ret);
            setStartError(ret.status);
          }
        }
      }
    })();
  }, [document, windowUrl.current, activeLogUrl, params]);

  // Error checking...
  if (startError) return <ErrorPage message={'Error starting log'} />; // Eg. api is down
  if (deploymentQuery.isError) return <ErrorPage message="Connection error" />; // Eg. api is down
  if (deploymentQuery.isLoading) return <LoadingPage />;
  if (deploymentQuery.data?.status == 'deployment not found') return <ErrorPage message="Deployment not found" />;
  if (!activeLog) {
    return <ErrorPage message={JSON.stringify(deploymentQuery.error)} />;
  }

  return (
    <main css={mainStyle}>
      <LogView isDialog={false} />
    </main>
  );
};

const mainStyle = (theme: Theme) => css`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  > .MuiListItem-root {
    padding-bottom: 16px;
    background-color: black;
  }

  > .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    padding: 0;
  }
`;

const errorStyle = (theme: Theme) => css`
  color: ${theme.color.white};
  padding: 2rem;
  text-align: center;
`;
